import { useApiContext } from "@/store/providers/ApiProvider";
import { RequestError } from "@/utilities/types/requests";
import { useCallback, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";

export interface UseApiProps<T> {
  request: ((...args: any[]) => Promise<T>) | null;
  enableToast?: boolean;
  reValidate?: boolean;
  key?: string;
}

export default function useApiStore<T>({
  request,
  enableToast = false,
  reValidate = true,
  key,
}: UseApiProps<T>) {
  const [localLoading, setLocalLoading] = useState<boolean>(false);
  const [localResponse, setLocalResponse] = useState<T | null>(null);
  const [localError, setLocalError] = useState<RequestError>();

  const context = key ? useApiContext() : null;

  const prevArgs = useRef<any>(null);

  const setSharedState = (state: Partial<any>) => {
    if (key && context) {
      context.setState(key, state);
    }
  };

  const selectedState = key && context ? context.states[key] : undefined;

  const shared = useMemo((): Record<string, any> => {
    return key && context ? context.states[key] || {} : {};
  }, [selectedState, key]);

  const requestFunc = useCallback(
    async (...args: any[]) => {
      if (key) {
        setSharedState({ loading: true, error: null });
      } else {
        setLocalLoading(true);
        setLocalError(undefined);
      }

      if (reValidate) {
        if (key) {
          setSharedState({ response: null });
        } else {
          setLocalResponse(null);
        }
      }
      let toastId;
      if (enableToast) {
        toastId = toast.loading("Loading...");
      }

      try {
        if (typeof request !== "function") {
          return;
        }
        const response = await request(...args);
        if (key) {
          setSharedState({ response, args });
        } else {
          setLocalResponse(response);
        }
        if (enableToast) {
          toast.success("Success", { id: toastId });
        }
        prevArgs.current = args;
        return response;
      } catch (error: any) {
        if (key) {
          setSharedState({ error: error as RequestError });
        } else {
          setLocalError(error as RequestError);
        }
        if (enableToast) {
          toast.error(error?.message || "Something went wrong!", {
            id: toastId,
          });
        }
        throw error;
      } finally {
        if (key) {
          setSharedState({ loading: false });
        } else {
          setLocalLoading(false);
        }
      }
    },
    [request, key]
  );

  return {
    loading: key ? shared?.loading ?? false : localLoading,
    request: requestFunc,
    refetch: async () => {
      const args = key ? shared?.args ?? [] : prevArgs.current ?? [];
      return await requestFunc(...args);
    },
    response: key ? shared?.response ?? null : localResponse,
    error: key ? shared?.error : localError,
  };
}
